import { http } from "@/api/http";
import type { AxiosRequestConfig } from "axios";

import type {
  AddApplicationResult,
  ApplicationListResult,
  DailyRoleScanResponse,
  RoleListResult,
  RoleFilterOptionsResult,
  RoleListQuery,
  RoleRecord,
  SaveRoleResult,
  SponsorSearchFilters,
  SponsorSearchResult,
} from "@/types/rolesSkilled";

function signalConfig(signal?: AbortSignal): AxiosRequestConfig {
  const config: AxiosRequestConfig = {};
  if (signal) config.signal = signal;
  return config;
}

export async function getRoles(query: RoleListQuery, signal?: AbortSignal): Promise<RoleListResult> {
  const config: AxiosRequestConfig = {
    params: {
      ...query.filters,
      direction: query.direction,
      page: query.page,
      page_size: query.page_size,
      sort: query.sort,
    },
  };
  if (signal) config.signal = signal;
  const response = await http.get<RoleListResult>("/roles", config);
  return response.data;
}

export async function getRolePreview(signal?: AbortSignal): Promise<RoleListResult> {
  const response = await http.get<RoleListResult>("/roles/preview", signalConfig(signal));
  return response.data;
}

export async function getRoleFilterOptions(signal?: AbortSignal): Promise<RoleFilterOptionsResult> {
  const response = await http.get<RoleFilterOptionsResult>("/roles/filter-options", signalConfig(signal));
  return response.data;
}

export async function getApplications(signal?: AbortSignal): Promise<ApplicationListResult> {
  const response = await http.get<ApplicationListResult>("/applications", signalConfig(signal));
  return response.data;
}

export async function addApplication(roleId: string, signal?: AbortSignal): Promise<AddApplicationResult> {
  const response = await http.post<AddApplicationResult>("/applications/add", {
    role_id: roleId,
  }, signalConfig(signal));
  return response.data;
}

export async function saveRole(role: RoleRecord, signal?: AbortSignal): Promise<SaveRoleResult> {
  const response = await http.post<SaveRoleResult>("/roles/save", { role }, signalConfig(signal));
  return response.data;
}

export async function startDailyRoleScan(signal?: AbortSignal): Promise<DailyRoleScanResponse> {
  const response = await http.post<DailyRoleScanResponse>("/roles/daily", {}, signalConfig(signal));
  return response.data;
}

export async function getDailyRoleScanStatus(jobId: string, signal?: AbortSignal): Promise<DailyRoleScanResponse> {
  const config: AxiosRequestConfig = {
    params: { job_id: jobId },
  };
  if (signal) config.signal = signal;
  const response = await http.get<DailyRoleScanResponse>("/roles/daily/status", config);
  return response.data;
}

export async function cancelDailyRoleScan(jobId: string, signal?: AbortSignal): Promise<DailyRoleScanResponse> {
  const response = await http.post<DailyRoleScanResponse>("/roles/daily/cancel", {
    job_id: jobId,
  }, signalConfig(signal));
  return response.data;
}

export async function searchSkilledWorkerSponsors(
  filters: SponsorSearchFilters,
  signal?: AbortSignal,
): Promise<SponsorSearchResult> {
  const config: AxiosRequestConfig = {
    params: {
      limit: filters.limit,
      offset: filters.offset,
      q: filters.organisation_name,
      rating: filters.rating,
      route: filters.route,
      town_city: filters.town_city,
    },
    timeout: 60_000,
  };
  if (signal) config.signal = signal;
  const response = await http.get<SponsorSearchResult>("/skilled-worker-list", config);
  return response.data;
}
